import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope, faCheckCircle, faTimesCircle, faRotate, faEye,
  faBuilding, faFileAlt, faGlobe, faServer, faCode, faChartPie,
  faMoneyBillTransfer, faSearch, faXmark, faFilter,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import MerchantMultiSelect from '../../components/shared/MerchantMultiSelect';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const TABLE_ACTION_BTN =
  'px-2.5 py-1.5 text-[11px] rounded-lg border transition-colors flex items-center gap-1';

const EVENT_COLOR: Record<string, string> = {
  'order.paid': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  'order.expired': 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
  'payout.approved': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  'payout.completed': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  'payout.rejected': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  'webhook.test': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
};

const EventBadge: React.FC<{ event?: string }> = ({ event }) => {
  if (!event) return <span className="text-gray-400 dark:text-slate-500 text-xs">-</span>;
  const cls = EVENT_COLOR[event] || 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {event}
    </span>
  );
};

const DEFAULT_PAGE_SIZE = 50;

const WebhookLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const pageRef = useRef({ page: 1, size: DEFAULT_PAGE_SIZE });

  const [statusFilter, setStatusFilter] = useState('');
  const [merchantFilter, setMerchantFilter] = useState('');
  const [logTypeFilter, setLogTypeFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateTo, setAppliedDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));

  const [merchants, setMerchants] = useState<any[]>([]);

  const loadMerchants = useCallback(async () => {
    try {
      const response = await merchantApi.get('/merchants');
      setMerchants(response.data.data || []);
    } catch {
      console.error('Error loading merchants');
    }
  }, []);

  const abortControllerRef = useRef<AbortController | null>(null);

  const buildParams = useCallback((page: number, size: number) => {
    const params = new URLSearchParams();
    params.set('limit', String(size));
    params.set('offset', String((page - 1) * size));
    if (statusFilter) params.set('status', statusFilter);
    if (merchantFilter) params.set('merchant_id', merchantFilter);
    if (logTypeFilter) params.set('log_type', logTypeFilter);
    if (eventFilter) params.set('event', eventFilter);
    if (searchQuery.trim()) {
      params.set('search', searchQuery.trim());
    } else {
      if (appliedDateFrom) params.set('start_date', appliedDateFrom);
      if (appliedDateTo) params.set('end_date', appliedDateTo);
    }
    return params.toString();
  }, [statusFilter, merchantFilter, logTypeFilter, eventFilter, searchQuery, appliedDateFrom, appliedDateTo]);

  const fetchLogs = useCallback(async (page: number, size: number) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      setLoading(true);
      const response = await merchantApi.get(`/admin/webhook-logs?${buildParams(page, size)}`, { signal: controller.signal });
      if (!controller.signal.aborted) {
        setLogs(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
        toast.error('ไม่สามารถโหลดข้อมูลได้');
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [buildParams]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    pageRef.current.page = page;
    fetchLogs(page, pageRef.current.size);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    pageRef.current = { page: 1, size };
    fetchLogs(1, size);
  };

  useEffect(() => {
    loadMerchants();
  }, [loadMerchants]);

  useEffect(() => {
    setCurrentPage(1);
    pageRef.current.page = 1;
    fetchLogs(1, pageRef.current.size);
  }, [fetchLogs]);

  const handleSearch = () => {
    if (dateFrom && dateTo && dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setSearchQuery('');
    setCurrentPage(1);
    pageRef.current.page = 1;
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
  };

  const handleReset = () => {
    const today = dayjs().tz(TZ).format('YYYY-MM-DD');
    setStatusFilter('');
    setMerchantFilter('');
    setLogTypeFilter('');
    setEventFilter('');
    setSearchQuery('');
    setDateFrom(today);
    setDateTo(today);
    setCurrentPage(1);
    pageRef.current.page = 1;
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
  };

  const successCount = logs.filter(l => l.success).length;
  const failedCount = logs.filter(l => !l.success).length;
  const successRate = logs.length > 0 ? ((successCount / logs.length) * 100).toFixed(1) : '0';

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faEnvelope,
      iconStyle: { background: 'var(--theme-accent)' },
      iconClassName: 'text-white',
      label: 'Total',
      value: <span style={{ color: 'var(--theme-accent)' }}>{totalCount.toLocaleString()}</span>,
    },
    {
      icon: faCheckCircle,
      iconClassName: 'bg-green-500 text-white',
      label: 'Success (หน้านี้)',
      value: <span className="text-green-600 dark:text-green-400">{successCount.toLocaleString()}</span>,
    },
    {
      icon: faTimesCircle,
      iconClassName: 'bg-red-500 text-white',
      label: 'Failed (หน้านี้)',
      value: <span className="text-red-600 dark:text-red-400">{failedCount.toLocaleString()}</span>,
    },
    {
      icon: faChartPie,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'Success Rate (หน้านี้)',
      value: <span style={{ color: 'var(--theme-primary)' }}>{successRate}%</span>,
    },
  ], [totalCount, successCount, failedCount, successRate]);

  const columns: Column<any>[] = useMemo(() => [
    {
      key: 'merchant',
      header: 'Merchant',
      render: (log) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
            <FontAwesomeIcon icon={faBuilding} className="text-xs" />
          </div>
          <div>
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm">{log.merchant?.name || '-'}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500">{log.merchant?.email || ''}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'ref',
      header: 'Reference',
      render: (log) => {
        if (log.payout) {
          return (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400">
                <FontAwesomeIcon icon={faMoneyBillTransfer} className="text-xs" />
              </div>
              <div>
                <p className="font-medium text-gray-800 dark:text-slate-100 text-sm">{log.payout.payout_number}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">฿{log.payout.amount?.toLocaleString()}</p>
              </div>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
              <FontAwesomeIcon icon={faFileAlt} className="text-xs" />
            </div>
            <div>
              <p className="font-medium text-gray-800 dark:text-slate-100 text-sm">{log.order?.order_number || '-'}</p>
              <p className="text-xs text-gray-500 dark:text-slate-400">฿{log.order?.total_amount || 0}</p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'event',
      header: 'Event',
      render: (log) => <EventBadge event={log.event} />,
    },
    {
      key: 'webhook_url',
      header: 'Webhook URL',
      render: (log) => (
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faGlobe} className="text-gray-400 dark:text-slate-500 text-xs" />
          <span className="text-xs text-gray-600 dark:text-slate-300 max-w-[200px] truncate" title={log.webhook_url}>
            {log.webhook_url}
          </span>
        </div>
      ),
    },
    {
      key: 'success',
      header: 'Status',
      sortable: true,
      render: (log) => (
        log.success ? (
          <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faCheckCircle} /> Success
          </span>
        ) : (
          <span className="px-2.5 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faTimesCircle} /> Failed
          </span>
        )
      ),
    },
    {
      key: 'response_status',
      header: 'Response',
      sortable: true,
      render: (log) => (
        <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200 px-2 py-1 rounded">
          {log.response_status || '-'}
        </code>
      ),
    },
    {
      key: 'retry_count',
      header: 'Retry',
      sortable: true,
      render: (log) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${log.retry_count > 0 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'}`}>
          {log.retry_count}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'วันที่',
      sortable: true,
      render: (log) => (
        <div>
          <p className="text-sm text-gray-700 dark:text-slate-300">
            {new Date(log.created_at).toLocaleDateString('th-TH')}
          </p>
          <p className="text-xs text-gray-400 dark:text-slate-500">
            {new Date(log.created_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (log) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLog(log);
          }}
          className={`${TABLE_ACTION_BTN} border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600`}
        >
          <FontAwesomeIcon icon={faEye} /> ดู
        </button>
      ),
    },
  ], []);

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faEnvelope}
        title="Webhook Logs"
        subtitle={`System-wide Webhook Logs • ${totalCount.toLocaleString()} รายการทั้งหมด`}
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <button
            type="button"
            onClick={() => fetchLogs(pageRef.current.page, pageRef.current.size)}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      >
        {/* Search */}
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            ค้นหา
          </label>
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="ค้นหา Order No., Payout No. ..."
              className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500 dark:focus:ring-slate-600 dark:focus:border-slate-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
              >
                <FontAwesomeIcon icon={faXmark} className="text-sm" />
              </button>
            )}
          </div>
        </div>

        {/* ประเภท */}
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faFileAlt} className="mr-1" />
            ประเภท
          </label>
          <select
            value={logTypeFilter}
            onChange={(e) => setLogTypeFilter(e.target.value)}
            className={FILTER_SELECT_CLASS}
          >
            <option value="">ทั้งหมด</option>
            <option value="order">Order</option>
            <option value="payout">Payout</option>
          </select>
        </div>

        {/* Event */}
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faEnvelope} className="mr-1" />
            Event
          </label>
          <select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className={FILTER_SELECT_CLASS}
          >
            <option value="">ทั้งหมด</option>
            <optgroup label="Order">
              <option value="order.paid">order.paid</option>
              <option value="order.expired">order.expired</option>
            </optgroup>
            <optgroup label="Payout">
              <option value="payout.approved">payout.approved</option>
              <option value="payout.completed">payout.completed</option>
              <option value="payout.rejected">payout.rejected</option>
            </optgroup>
          </select>
        </div>

        {/* Status */}
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faFilter} className="mr-1" />
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={FILTER_SELECT_CLASS}
          >
            <option value="">ทั้งหมด</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        {/* Merchant */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faBuilding} className="mr-1" />
            Merchant
          </label>
          <MerchantMultiSelect
            merchants={merchants}
            value={merchantFilter}
            onChange={(id) => setMerchantFilter(id)}
            placeholder="All Merchants"
            variant="light"
          />
        </div>
      </UiFilterPanel>

      <UiStatsGrid cards={statCards} loading={loading && logs.length === 0} cols={4} />

      <DataTable
        data={logs}
        columns={columns}
        loading={loading}
        emptyMessage="ไม่มี Webhook Logs"
        emptyIcon={<FontAwesomeIcon icon={faEnvelope} className="text-5xl text-gray-200 dark:text-slate-600" />}
        serverPagination={{
          currentPage,
          totalItems: totalCount,
          pageSize,
          pageSizeOptions: [20, 50, 100, 200],
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
        }}
        hoverable
        compact
      />

      <UiModal
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title="Webhook Log Detail"
        icon={faCode}
        iconColor="text-blue-500"
        size="xl"
        scrollable
        footer={
          <UiButton variant="secondary" onClick={() => setSelectedLog(null)} className="w-full justify-center">
            ปิด
          </UiButton>
        }
      >
        {selectedLog && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              {selectedLog.event && <EventBadge event={selectedLog.event} />}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
                <p className="text-xs text-gray-500 dark:text-slate-400">Status</p>
                {selectedLog.success ? (
                  <span className="text-green-600 dark:text-green-400 font-semibold flex items-center gap-1">
                    <FontAwesomeIcon icon={faCheckCircle} /> Success
                  </span>
                ) : (
                  <span className="text-red-600 dark:text-red-400 font-semibold flex items-center gap-1">
                    <FontAwesomeIcon icon={faTimesCircle} /> Failed
                  </span>
                )}
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
                <p className="text-xs text-gray-500 dark:text-slate-400">Response Status</p>
                <code className="font-semibold text-gray-800 dark:text-slate-100">{selectedLog.response_status || '-'}</code>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
                <p className="text-xs text-gray-500 dark:text-slate-400">Retry Count</p>
                <p className="font-semibold text-gray-800 dark:text-slate-100">{selectedLog.retry_count}</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
                <p className="text-xs text-gray-500 dark:text-slate-400">Time</p>
                <p className="font-semibold text-sm text-gray-800 dark:text-slate-100">
                  {new Date(selectedLog.created_at).toLocaleString('th-TH')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedLog.merchant && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3">
                  <p className="text-xs text-blue-500 dark:text-blue-400 mb-1">Merchant</p>
                  <p className="font-semibold text-blue-800 dark:text-blue-200">{selectedLog.merchant.name}</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">{selectedLog.merchant.email}</p>
                </div>
              )}
              {selectedLog.payout ? (
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-3">
                  <p className="text-xs text-orange-500 dark:text-orange-400 mb-1">Payout</p>
                  <p className="font-semibold text-orange-800 dark:text-orange-200">{selectedLog.payout.payout_number}</p>
                  <p className="text-xs text-orange-600 dark:text-orange-400">
                    ฿{selectedLog.payout.amount?.toLocaleString()} · {selectedLog.payout.bank_name} · {selectedLog.payout.status}
                  </p>
                </div>
              ) : selectedLog.order ? (
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3">
                  <p className="text-xs text-purple-500 dark:text-purple-400 mb-1">Order</p>
                  <p className="font-semibold text-purple-800 dark:text-purple-200">{selectedLog.order.order_number}</p>
                  <p className="text-xs text-purple-600 dark:text-purple-400">฿{selectedLog.order.total_amount}</p>
                </div>
              ) : null}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faServer} className="text-blue-500 dark:text-blue-400" />
                <h3 className="font-semibold text-gray-800 dark:text-slate-100">Request Body</h3>
              </div>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-xl overflow-x-auto text-xs max-h-48">
                {JSON.stringify(selectedLog.request_body, null, 2)}
              </pre>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faCode} className="text-green-500 dark:text-green-400" />
                <h3 className="font-semibold text-gray-800 dark:text-slate-100">Response Body</h3>
              </div>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-xl overflow-x-auto text-xs max-h-48">
                {selectedLog.response_body || 'No response body'}
              </pre>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faGlobe} className="text-purple-500 dark:text-purple-400" />
                <h3 className="font-semibold text-gray-800 dark:text-slate-100">Webhook URL</h3>
              </div>
              <code className="block bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200 p-3 rounded-xl text-sm break-all">
                {selectedLog.webhook_url}
              </code>
            </div>
          </>
        )}
      </UiModal>
    </div>
  );
};

export default WebhookLogsPage;
